<script setup lang="ts">
import type { PublicRuntimeConfig } from 'nuxt/schema'
import type { User, UserManager } from 'oidc-client-ts'
import type { PrivacySettings, UserGrant, UserWithProfile } from '../helper/types'
import { authentication, manualPublicApi } from '@d/sdk'
import { useTheme } from 'vuetify'
import { getPrivacySettings, getTheming, getUserGrants, getUserPermissions } from '../helper/api'
import { createAuth, PrivacySettingsSymbol, ProjectGrantSymbol, UserGrantSymbol, UserManagerSymbol, UserPermissionSymbol, UserSymbol } from '../helper/auth'
import { handleLocale } from '../helper/lang'
import { Scopes } from '../helper/scopes/enum'
import { curTheme, overwriteThemes } from '../helper/theming'

// set inside v-app directly to prevent rerendering because of a delay if you set the theme globally
const userRef = shallowRef<UserWithProfile | undefined>(undefined)
const userGrants = shallowRef<UserGrant[] | undefined>(undefined)
const userPermissions = shallowRef<string[]>([] as string[])
const privacySettings = shallowRef<PrivacySettings | undefined>(undefined)
const projectGrants = shallowRef<string[]>([])
const userManager = shallowRef<UserManager | undefined>(undefined)
const theme = useTheme()

const route = useRoute('client')
const manualRoute = useRoute('manual-shared-tenant-key')
const router = useRouter()
const config = useRuntimeConfig().public

if (!['logout', 'swagger', 'manual-shared-tenant-key-doc', 'manual-shared-tenant-key', 'manual-shared-tenant-key-print', 'share-shareId'].includes(route.name as any)) {
  /**
   * Provide some objects to use in the components
   */
  provide(PrivacySettingsSymbol, privacySettings)
  provide(UserSymbol, userRef)
  provide(UserGrantSymbol, userGrants)
  provide(UserManagerSymbol, userManager)
  provide(UserPermissionSymbol, userPermissions)
  provide(ProjectGrantSymbol, projectGrants)

  let client = route.params.client

  if (route.name as any === 'index' && typeof route.query.state === 'string' && typeof route.query.code === 'string') {
    let stateid = route.query.state
    const sepIdx = stateid.indexOf(';')
    if (sepIdx >= 0) {
      stateid = route.query.state.substring(0, sepIdx)
    }
    const oidcData = window.localStorage.getItem(`oidc.${stateid}`)
    if (oidcData !== null) {
      try {
        const d = JSON.parse(oidcData)
        if (typeof d.client_id === 'string') {
          client = d.client_id as string

          const hashIdx = d.url_state.indexOf('#')
          let hash
          if (hashIdx >= 0) {
            hash = d.url_state.substring(hashIdx)
          }

          await navigateTo({ name: 'client', params: { client }, query: route.query, hash })
        }
      }
      catch (e) {
        console.error(e)
      }
    }
  }

  if (client === undefined) {
    await navigateTo({ name: 'index' })
  }
  else {
    const manager = createAuth(config, client)

    const username = typeof route.query.username === 'string' && route.query.username.length > 0 ? route.query.username : undefined

    let user: User | null = null

    try {
      if (typeof route.query.state === 'string' && typeof route.query.code === 'string') {
        try {
          user = await manager.signinRedirectCallback()
        }
        finally {
          const query = Object.assign({}, route.query)
          delete query.state
          delete query.code

          // even if it fails, we don't want it to be retried with the same state/code
          await router.replace({ query, hash: route.hash })
        }
      }
      else {
        user = await manager.getUser()
      }
    }
    catch (e) {
      console.error('user retrieval failed', e)
    }

    // signing redirect if necessary, hint last logged in username
    if (user == null || (user != null && user.expired) || (username != null && user.profile.preferred_username !== username)) {
      const urlState = `${route.path}${route.hash}`
      await manager.signinRedirect({ login_hint: username ?? (user == null ? undefined : user.profile.preferred_username), url_state: urlState })
    }
    else {
      await authSuccess(manager, config, user)
      const urlState = user.url_state
      if (urlState) {
        const hashIdx = urlState.indexOf('#')
        let path = urlState
        let hash
        if (hashIdx >= 0) {
          path = urlState.substring(0, hashIdx)
          hash = urlState.substring(hashIdx)
        }
        router.replace({ path, hash })
      }
      else if (route.hash) {
        router.replace({ hash: route.hash })
      }
    }
  }
}
else {
  await authDisabled()
}

async function authDisabled(): Promise<void> {
  // use saved locale
  handleLocale()

  let tenant
  let dontTheme = false
  if (['manual-shared-tenant-key-doc', 'manual-shared-tenant-key', 'manual-shared-tenant-key-print'].includes(route.name as any)) {
    tenant = manualRoute.params.tenant
    try {
      const themeSettings = await manualPublicApi.getThemeSettings(tenant, manualRoute.params.key)
      dontTheme = themeSettings.useStandardTheme
    }
    catch {
      dontTheme = true
    }
  }

  if (!dontTheme) {
    try {
      // try to get the theming from the supplied personal access token
      // const orgid = config.manual_theme_orgid.length > 0 ? config.manual_theme_orgid : undefined
      const theming = await getTheming(config.manual_theme_access_token, config.authority, tenant)
      overwriteThemes(theming, theme, curTheme)
    }
    catch {
      const isDark = useDark()
      watchEffect(() => {
        curTheme.value = isDark.value ? 'dark' : 'light'
      })
    }
  }
  else {
    const isDark = useDark()
    watchEffect(() => {
      curTheme.value = isDark.value ? 'dark' : 'light'
    })
  }

  // default dark or light theme
}

async function authSuccess(manager: UserManager, config: PublicRuntimeConfig, user: User): Promise<void> {
  authentication(user.access_token, user.profile.aud[user.profile.aud.length - 2], user.profile.name ?? '')
  userManager.value = manager
  userRef.value = user as UserWithProfile
  projectGrants.value = Object.keys(userRef.value.profile['urn:zitadel:iam:org:project:roles'])

  handleLocale(user.profile.locale)

  const roles = userRef.value.profile[Scopes.ProjectRoles]
  const orgid = Object.keys(roles.read)[0]

  try {
    // Start these requests in parallel to save time
    await Promise.all([
      getTheming(user.access_token, config.authority, orgid).then(e => overwriteThemes(e, theme, curTheme)),
      getUserGrants(user.access_token, config.authority).then(e => userGrants.value = e),
      getPrivacySettings(user.access_token, config.authority).then(e => privacySettings.value = e),
      getUserPermissions(user.access_token, config.authority).then(e => userPermissions.value = e),
    ])
  }
  catch (e) {
    // typically a timeout when the token is invalid but zitadel does not end the requests correctly
    // this bug only occurs if http2 is used
    console.error('api call to zitadel failed', e)
    await manager.removeUser()
    await manager.signinRedirect({ login_hint: user.profile.preferred_username })
  }
}
</script>

<template>
  <v-app :theme="curTheme" class="content">
    <slot class="content" />
  </v-app>
</template>

<style scoped>
.content{
  scrollbar-color: rgba(var(--v-theme-primary), 0.3) rgba(var(--v-theme-primary), 0.05);
}
</style>
